import { useCallback, useEffect, useState } from "react";

import useQueryClient from "../api/query";
import type { AppConfig } from "../api/query/queryClient";

const DEFAULT_CONFIG: AppConfig = {
  featureFlags: {
    reports: false,
    insuranceAr: false,
  },
  deniseDueNowDaysThreshold: 30,
  posthog: {
    analyticsEnabled: true,
    replayEnabled: true,
    replayBaselineSampleRate: 1,
    replayExcludedOrgIds: [],
  },
  unpostBlockedPracticeIds: [],
};

type ConfigState = {
  config: AppConfig;
  isLoading: boolean;
};

let configCache: ConfigState | null = null;
let configFetchPromise: Promise<ConfigState> | null = null;

export function useConfig(): ConfigState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ConfigState>(
    () =>
      configCache || {
        config: DEFAULT_CONFIG,
        isLoading: true,
      },
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding queryClient would change when this runs
  const fetchConfig = useCallback(async () => {
    if (configFetchPromise) {
      return configFetchPromise;
    }

    if (configCache) {
      return configCache;
    }

    setState((prev) => ({ ...prev, isLoading: true }));

    configFetchPromise = queryClient
      .getAppConfig()
      .then((config) => {
        const newState: ConfigState = { config, isLoading: false };
        setState(newState);
        configCache = newState;
        return newState;
      })
      .catch((error) => {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to fetch config:", error);
        const fallback: ConfigState = {
          config: DEFAULT_CONFIG,
          isLoading: false,
        };
        setState(fallback);
        return fallback;
      })
      .finally(() => {
        configFetchPromise = null;
      });

    return configFetchPromise;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const newState = await fetchConfig();
        setState(newState);
        configCache = newState;
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to fetch config:", error);
        setState({ config: DEFAULT_CONFIG, isLoading: false });
      }
    })();
  }, [fetchConfig]);

  return state;
}

export function useConfigValue<K extends keyof AppConfig>(
  key: K,
): { value: AppConfig[K]; isLoading: boolean } {
  const { config, isLoading } = useConfig();
  return { value: config[key], isLoading };
}
